<template>
  <router-link
    :to="projectRoute"
    class="project flex flex-col text-sm justify-between items-center cursor-pointer text-black"
    :class="classes"
    @click.native.prevent="gotoProject"
    event
  >
    <div class="w-full flex justify-between py-3 px-2">
      <div class="flex items-center">
        <div class="flex flex-col xxl:flex-row items-start xxl:items-center">
          <div class="flex items-center">
            <span class="w-16">{{ data.id }}</span>
            <span class="flex items-center">
              <span
                class="flex justify-start items-center mr-1"
                v-if="data.application !== ''"
              >
                <img
                  :src="data.application.icon"
                  alt="Application"
                  width="auto"
                  height="auto"
                  class="w-6 h-6 p-1"
                  v-if="data.application.icon !== null"
                />
                <img
                  :src="data.application.renderer.icon"
                  alt="Renderer"
                  width="auto"
                  height="auto"
                  class="w-6 h-6 p-1"
                  v-if="data.application.renderer.icon !== null"
                />
              </span>
            </span>
          </div>
          <span
            class="xxl:ml-2 pt-1 xxl:pt-0"
            v-text="data.filename"
            :class="{ 'text-gray-700 italic': !data.hasFilename }"
          ></span>
        </div>
      </div>
      <div class="flex items-center justify-end capitalize">
        <div
          class="flex flex-col-reverse xl:flex-row items-end xl:items-center"
        >
          <div v-if="data.qstatus">
            <small
              v-if="data.qstatus.status"
              class="block font-bold"
              v-text="data.qstatus.status"
            />
            <progress-bar
              v-if="data.qstatus.percent !== undefined"
              class="block"
              :progress="data.qstatus.percent"
            />
            <small
              v-if="data.qstatus.elapsed && false"
              class="block text-right"
              v-text="data.qstatus.elapsed"
            />
          </div>
          <div v-else-if="uploadProgress">
            <progress-bar
              class="block"
              :progress="
                Math.round(
                  (100 * uploadProgress.bytesUploaded) /
                    uploadProgress.bytesTotal
                )
              "
            />
          </div>
          <div v-else class="w-32 pt-1 xl:pt-0 text-gray-700">
            {{ data.date.date }}
          </div>
          <div class="flex items-center justify-end">
            <div class="min-w-20 text-right pr-3">
              <span v-if="data.cost.raw > 0" v-html="data.cost.html" />
              <span v-else-if="data.cost.ghz_h > 0"
                >{{ Math.round(data.cost.ghz_h)
                }}<small v-if="data.priority.price_unit">
                  {{ data.priority.price_unit }}</small
                ></span
              >
            </div>
            <router-link
              :to="{
                name: statusRoute,
                params: {
                  id: data.id,
                  locale: $route.params.locale,
                  type: $route.params.type,
                },
              }"
              class="w-32 whitespace-no-wrap text-xs py-1 status"
              :class="data.status"
              @click.native.stop
            >
              <div
                class="flex items-center justify-center relative"
                v-if="uploading"
              >
                <span v-text="statusLabel"></span>
                <span class="px-1">
                  <font-awesome-icon
                    icon="check"
                    v-if="uploadHasSuccess"
                    class="text-green-700"
                  />
                  <font-awesome-icon
                    icon="exclamation-circle"
                    v-else-if="uploadHasError"
                    class="text-red-800"
                  />
                  <font-awesome-icon
                    icon="spinner"
                    v-else-if="uploadHasRunning"
                    :spin="!uploadHasRunning.paused"
                  />
                </span>
              </div>
              <div
                class="flex items-center justify-center"
                v-else-if="data.error || data.missingFrames"
                v-tooltip="
                  data.missingFrames
                    ? $store.state.dashboard.i18n.project.errors
                        .missing_frames +
                      ' ' +
                      data.status_error.label
                    : data.status_error.label
                "
              >
                <font-awesome-icon
                  class="mr-2 text-red-800"
                  icon="exclamation-circle"
                />
                {{ statusLabel }}
              </div>
              <span v-else v-text="statusLabel" />
              <span v-if="data.rank" v-text="' #' + data.rank" />
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </router-link>
</template>

<script>
import { flash } from "@/boot/functions";
import { mapState } from "vuex";
import ProgressBar from "../Design/ProgressBar";

import useSWRV from "@/store/swrv";

export default {
  props: ["data", "bgblue"],
  components: { ProgressBar },
  data() {
    return {
      id: this.data.id,
    };
  },
  setup(props) {
    const { reload } = useSWRV(
      () =>
        props.data &&
        ["uploaded", "inwaiting", "inprogress"].includes(props.data.status) &&
        "/api/app/projects/" + props.data.id
    );
    return { reload };
  },
  methods: {
    updateProject() {
      if (this.isSelected) {
        this.reload();
      } else {
        this.$store.dispatch("dashboard/getProject", this.id);
      }
    },

    startValidatingLoop(newStatus, mounted) {
      if (newStatus === "uploaded" && !this.t) {
        if (!mounted) {
          flash(
            this.yourProject + this.i18n.successes.has_been_uploaded,
            "success",
            true,
            true
          );
        }

        this.t = setInterval(() => {
          // console.log('validating - refresh');

          this.updateProject();
        }, this.refreshRateValidating);
      } else {
        clearInterval(this.t);

        this.startQueuedLoop(newStatus, false);
      }
    },

    startQueuedLoop(status, mounted) {
      if (status === "inwaiting") {
        if (!mounted) {
          flash(
            this.yourProject + this.i18n.successes.has_been_validated,
            "info",
            true,
            true
          );
        }

        this.t = setInterval(() => {
          // console.log('queued - refresh');

          this.updateProject();
        }, this.refreshRateQueued);
      } else {
        clearInterval(this.t);

        this.startRenderingLoop(status, mounted);
      }
    },

    startRenderingLoop(status, mounted) {
      if (status === "inprogress") {
        if (!mounted) {
          flash(
            this.yourProject + this.i18n.successes.has_started_rendering,
            "info",
            true,
            true
          );
        }

        this.t = setInterval(() => {
          // console.log('rendering - refresh');

          this.updateProject();
        }, this.refreshRateRendering);
      } else {
        clearInterval(this.t);
      }
    },
    gotoProject() {
      if (
        this.data.id == this.$route.params.id &&
        this.$route.name == "project.index"
      ) {
        this.$router.push({
          name: "welcome",
          params: {
            locale: this.$route.params.locale,
            type: this.$route.params.type,
          },
        });
        return;
      }
      this.$router.push(this.projectRoute);
    },
  },
  computed: {
    ...mapState({
      refreshRateValidating: (state) =>
        state.dashboard.projects.refreshRates.validating,
      refreshRateRendering: (state) =>
        state.dashboard.projects.refreshRates.rendering,
      refreshRateQueued: (state) =>
        state.dashboard.projects.refreshRates.queued,
    }),
    projectRoute() {
      return {
        name: "project.index",
        params: {
          id: this.data.id,
          locale: this.$route.params.locale,
          type: this.$route.params.type,
        },
      };
    },
    statusRoute() {
      const status = this.data.status;
      if (status === "created") {
        return this.data.priority && this.data.priority.id
          ? "project.upload"
          : "project.priority";
      }
      if (status === "uploading") {
        return "project.upload";
      }
      if (status === "inwaiting") {
        return "project.priority";
      }
      if (status === "finished") {
        return "project.download";
      }
      if (status === "invalid") {
        return "project.logs";
      }
      return "project.index";
    },
    isSelected() {
      return this.id == this.$route.params.id;
    },

    status() {
      return this.data.status;
    },

    statusLabel() {
      return this.$store.state.dashboard.projects.statuses[this.status];
    },

    classes() {
      if (this.isSelected) {
        return "bg-gray-400";
      }
      if (this.bgblue) {
        return "bg-blue-200 hover:bg-gray-300";
      }

      return "hover:bg-gray-300";
    },

    rendering() {
      return this.data.status === "inprogress";
    },

    uploading() {
      return this.data.status === "uploading";
    },

    inqueue() {
      return this.data.status === "inwaiting";
    },

    uploadAttempts() {
      return this.$store.getters["tuspload/ownAttempts"](this.id);
    },

    uploadHasError() {
      return this.uploadAttempts.find((a) => a.error);
    },
    uploadHasSuccess() {
      return this.uploadAttempts.find((a) => a.success);
    },
    uploadHasRunning() {
      return this.uploadAttempts.find((a) => a.upload && !a.success);
    },
    uploadProgress() {
      return this.uploadHasRunning && this.uploadHasRunning.progress;
    },

    stoppingRendering() {
      return this.rendering && this.data.status_error.id === "stopped_by_user";
    },

    removingFromQueue() {
      return this.rendering && this.data.status_error.id === "stopped_by_user";
    },

    i18n() {
      return this.$store.state.dashboard.i18n.project;
    },

    yourProject() {
      let text = this.$store.state.dashboard.i18n.project.your_project.replace(
        ":id",
        this.id
      );

      if (this.data.hasFilename) {
        text += " (<b>" + this.data.filename + "</b>)";
      }

      return text + " ";
    },
  },
  watch: {
    status: function (newStatus, oldStatus) {
      this.startValidatingLoop(newStatus, oldStatus);

      if (newStatus === "invalid" && oldStatus === "uploaded") {
        flash(
          this.yourProject + this.i18n.errors.has_failed_validation,
          "error",
          true,
          true
        );
      }

      if (newStatus === "invalid" && oldStatus === "inprogress") {
        flash(
          this.yourProject + this.i18n.errors.has_failed_rendering,
          "error",
          true,
          true
        );
      }

      if (newStatus === "finished") {
        flash(
          this.yourProject + this.i18n.successes.has_been_rendered,
          "success",
          true,
          true
        );
      }
    },
  },
  mounted() {
    if (this.data.status === "uploaded") {
      this.startValidatingLoop("uploaded", true);
    }

    if (this.data.status === "inwaiting") {
      this.startQueuedLoop("inwaiting", true);
    }

    if (this.data.status === "inprogress") {
      this.startRenderingLoop("inprogress", true);
    }
  },
};
</script>
